import { resolveFieldLabelAnchor } from '../geometry/resolve-field-label-anchor';
import { createSvgElement } from '../svg';
import type { MapFieldThemeModel, MapRenderFieldModel } from '../types';

/**
 * Builds the non-interactive SVG group for all field labels.
 *
 * @param params Fields and status themes from the render model.
 * @returns Detached group carrying the stable `data-map-layer="field-labels"` hook.
 */
export function renderFieldLabelsLayer(params: {
  fields: readonly MapRenderFieldModel[];
  fieldStatusTheme: Readonly<Record<MapRenderFieldModel['status'], MapFieldThemeModel>>;
}): SVGGElement {
  const layerElement = createSvgElement('g');
  layerElement.dataset.mapLayer = 'field-labels';
  layerElement.setAttribute('pointer-events', 'none');

  for (const field of params.fields) {
    layerElement.append(
      createFieldLabelElement({
        field,
        theme: params.fieldStatusTheme[field.status],
      }),
    );
  }

  return layerElement;
}

/**
 * Builds one semantic field label element for full rendering or a partial field update.
 *
 * @param params Field visual data and already-resolved status theme.
 * @returns Detached text element with the canonical field `data-map-entity-id` hook.
 */
export function createFieldLabelElement(params: {
  field: MapRenderFieldModel;
  theme: MapFieldThemeModel;
}): SVGTextElement {
  const anchorPoint = resolveFieldLabelAnchor({
    points: params.field.points,
    label: params.field.label,
  });
  const textElement = createSvgElement('text');
  textElement.textContent = params.field.label.text;
  textElement.setAttribute('x', String(anchorPoint.x));
  textElement.setAttribute('y', String(anchorPoint.y));
  textElement.setAttribute('fill', params.theme.textColor);
  textElement.setAttribute('font-size', String(params.field.label.size));
  textElement.setAttribute('font-family', 'ui-monospace, SFMono-Regular, Menlo, monospace');
  textElement.setAttribute('font-weight', '600');
  textElement.setAttribute('text-anchor', 'middle');
  textElement.setAttribute('dominant-baseline', 'middle');
  textElement.dataset.mapEntityKind = 'field-label';
  textElement.dataset.mapEntityId = String(params.field.id);

  if (params.field.label.direction === 'VERTICAL') {
    textElement.setAttribute('transform', `rotate(-90 ${anchorPoint.x} ${anchorPoint.y})`);
  }

  return textElement;
}
